/**
 * The signed-in layout (spec §5): a calm top bar with the feature navigation,
 * the theme toggle and the account menu — and the language toggle, hidden
 * while `LANGUAGE_SWITCH_ENABLED` is false (ruling 2026-09-28: Swedish only).
 * Mobile collapses the navigation into a single-column menu; nothing ever
 * scrolls horizontally.
 *
 * Nav entries come from the feature registry — this file is foundation code and
 * features never edit it.
 */
import clsx from "clsx";
import { ChevronDown, Languages, Menu, Moon, Sun, User as UserIcon, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { LANGUAGE_SWITCH_ENABLED, useT, useLang } from "@client/i18n";
import { Button } from "@client/components/ui";
import { navGroups, visibleNav, type NavGroup } from "./features";
import { useAuth } from "./auth";
import { useExamMode } from "./examMode";
import { useTheme } from "./theme";

/** True when the current path is inside any of the group's entries. */
function groupIsActive(group: NavGroup, pathname: string): boolean {
  return group.entries.some(
    (entry) => pathname === entry.to || pathname.startsWith(`${entry.to}/`),
  );
}

function navLinkClasses({ isActive }: { isActive: boolean }): string {
  return clsx(
    "rounded-lg px-3 py-2 text-sm font-medium transition-colors",
    isActive
      ? "bg-accent-100 text-accent-900 dark:bg-accent-900 dark:text-accent-50"
      : "text-ink-600 hover:bg-ink-100 hover:text-ink-900 dark:text-ink-300 dark:hover:bg-ink-800 dark:hover:text-ink-50",
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const t = useT();
  const { lang } = useLang();
  const { user, signOut, setLanguage } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { examActive } = useExamMode();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const accountRef = useRef<HTMLDivElement | null>(null);
  const menuToggleRef = useRef<HTMLButtonElement | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setAccountOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!openGroup) return undefined;
    const onClick = (event: MouseEvent) => {
      if (!navRef.current?.contains(event.target as Node)) setOpenGroup(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenGroup(null);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [openGroup]);

  // Escape closes the mobile drawer and gives focus back to its toggle (P0-9).
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMobileOpen(false);
      menuToggleRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  useEffect(() => {
    if (!accountOpen) return undefined;
    const onClick = (event: MouseEvent) => {
      if (!accountRef.current?.contains(event.target as Node)) setAccountOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAccountOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [accountOpen]);

  const entries = useMemo(
    () => visibleNav({ role: user?.role, examActive }),
    [user?.role, examActive],
  );
  const groups = useMemo(() => navGroups(entries), [entries]);

  return (
    <div className="flex min-h-full flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2 dark:focus:bg-ink-900"
      >
        {t("app.skipToContent")}
      </a>

      <header className="sticky top-0 z-30 border-b border-ink-200 bg-ink-50/90 backdrop-blur dark:border-ink-800 dark:bg-ink-950/90 no-print">
        <div className="mx-auto flex max-w-6xl items-center gap-2 px-4 py-2">
          <Link to="/home" className="mr-2 flex items-baseline gap-2">
            <span className="font-display text-lg text-ink-900 dark:text-ink-50">
              {t("app.title")}
            </span>
          </Link>

          <nav
            aria-label={t("app.title")}
            ref={navRef}
            className="hidden flex-1 items-center gap-1 md:flex"
          >
            <NavLink to="/home" className={navLinkClasses} end>
              {t("nav.home")}
            </NavLink>
            {groups.map((group) =>
              group.grouped ? (
                <div key={group.key} className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenGroup((open) => (open === group.key ? null : group.key))}
                    aria-expanded={openGroup === group.key}
                    aria-haspopup="menu"
                    className={clsx(
                      navLinkClasses({ isActive: groupIsActive(group, location.pathname) }),
                      "inline-flex items-center gap-1",
                    )}
                  >
                    {t(group.labelKey)}
                    <ChevronDown size={14} aria-hidden="true" />
                  </button>
                  {openGroup === group.key && (
                    <div role="menu" className="surface absolute left-0 mt-1 w-56 p-1 text-sm">
                      {group.entries.map((entry) => (
                        <NavLink
                          key={entry.to}
                          role="menuitem"
                          to={entry.to}
                          end={entry.end ?? false}
                          className="block rounded px-3 py-2 hover:bg-ink-100 dark:hover:bg-ink-800"
                        >
                          {t(entry.labelKey)}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={group.key}
                  to={group.to}
                  end={group.entries[0]?.end ?? false}
                  className={navLinkClasses}
                >
                  {t(group.labelKey)}
                </NavLink>
              ),
            )}
          </nav>

          <div className="ml-auto flex items-center gap-1">
            {LANGUAGE_SWITCH_ENABLED && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLanguage(lang === "sv" ? "en" : "sv")}
                aria-label={t("common.language")}
                title={t("common.language")}
                iconLeft={<Languages size={16} aria-hidden="true" />}
              >
                <span className="uppercase">{lang === "sv" ? "EN" : "SV"}</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="max-sm:min-h-11 max-sm:min-w-11"
              onClick={toggleTheme}
              aria-label={t("common.theme")}
              title={theme === "dark" ? t("common.theme.light") : t("common.theme.dark")}
            >
              {theme === "dark" ? (
                <Sun size={16} aria-hidden="true" />
              ) : (
                <Moon size={16} aria-hidden="true" />
              )}
            </Button>

            <div className="relative hidden md:block" ref={accountRef}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAccountOpen((open) => !open)}
                aria-expanded={accountOpen}
                aria-haspopup="menu"
                iconLeft={<UserIcon size={16} aria-hidden="true" />}
              >
                {user?.displayName ?? t("nav.account")}
              </Button>
              {accountOpen && (
                <div role="menu" className="surface absolute right-0 mt-1 w-56 p-1 text-sm">
                  <Link
                    role="menuitem"
                    to="/settings"
                    className="block rounded px-3 py-2 hover:bg-ink-100 dark:hover:bg-ink-800"
                  >
                    {t("nav.settings")}
                  </Link>
                  <Link
                    role="menuitem"
                    to="/hjalp"
                    className="block rounded px-3 py-2 hover:bg-ink-100 dark:hover:bg-ink-800"
                  >
                    {t("nav.help")}
                  </Link>
                  <button
                    role="menuitem"
                    type="button"
                    onClick={() => void signOut()}
                    className="block w-full rounded px-3 py-2 text-left hover:bg-ink-100 dark:hover:bg-ink-800"
                  >
                    {t("nav.signOut")}
                  </button>
                </div>
              )}
            </div>

            <Button
              ref={menuToggleRef}
              variant="ghost"
              size="sm"
              className="min-h-11 min-w-11 md:hidden"
              onClick={() => setMobileOpen((open) => !open)}
              aria-expanded={mobileOpen}
              aria-label={mobileOpen ? t("nav.closeMenu") : t("nav.openMenu")}
            >
              {mobileOpen ? (
                <X size={18} aria-hidden="true" />
              ) : (
                <Menu size={18} aria-hidden="true" />
              )}
            </Button>
          </div>
        </div>

        {mobileOpen && (
          <nav
            aria-label={t("nav.openMenu")}
            className="flex flex-col gap-1 border-t border-ink-200 px-4 py-2 md:hidden dark:border-ink-800"
          >
            <NavLink to="/home" className={navLinkClasses} end>
              {t("nav.home")}
            </NavLink>
            {groups.map((group) =>
              group.grouped ? (
                <div key={group.key} className="flex flex-col">
                  <span className="px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-ink-500 dark:text-ink-400">
                    {t(group.labelKey)}
                  </span>
                  {group.entries.map((entry) => (
                    <NavLink
                      key={entry.to}
                      to={entry.to}
                      end={entry.end ?? false}
                      className={(state) => clsx(navLinkClasses(state), "ml-3")}
                    >
                      {t(entry.labelKey)}
                    </NavLink>
                  ))}
                </div>
              ) : (
                <NavLink key={group.key} to={group.to} className={navLinkClasses}>
                  {t(group.labelKey)}
                </NavLink>
              ),
            )}
            <NavLink to="/settings" className={navLinkClasses}>
              {t("nav.settings")}
            </NavLink>
            <NavLink to="/hjalp" className={navLinkClasses}>
              {t("nav.help")}
            </NavLink>
            <button
              type="button"
              onClick={() => void signOut()}
              className="rounded-lg px-3 py-2 text-left text-sm font-medium text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-ink-800"
            >
              {t("nav.signOut")}
            </button>
          </nav>
        )}
      </header>

      {/*
        `tabindex={-1}`: a `<main>` is not natively focusable, so activating
        the skip link above (`href="#main"`) would scroll it into view without
        actually moving keyboard focus there — the next Tab press would
        restart from `<body>` instead of continuing past the header, which
        defeats the skip link's whole point. `-1` makes it a valid *script*
        focus target without adding it to the normal Tab order.
      */}
      <main
        id="main"
        tabIndex={-1}
        className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 outline-none"
      >
        {children}
      </main>

      <footer className="border-t border-ink-200 px-4 py-4 text-center text-xs text-ink-500 dark:border-ink-800 dark:text-ink-400 no-print">
        {t("landing.course")}
        <Link to="/integritet" className="ml-3 inline-block py-2 underline">
          {t("privacy.link")}
        </Link>
      </footer>
    </div>
  );
}
